import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import type { Tournament } from '@swiss-magic/shared-types';

// Shared publish-confirm dialog used by the dashboard card and the
// tournament hub. The checkbox decides publish-only vs publish + open
// registration in one step.

interface Props {
  target: Tournament | null;
  withRegistration: boolean;
  setWithRegistration: (checked: boolean) => void;
  onCancel: () => void;
  onConfirm: () => void;
}

export function PublishTournamentDialog({
  target,
  withRegistration,
  setWithRegistration,
  onCancel,
  onConfirm,
}: Props) {
  const { t } = useTranslation('director');
  return (
    <Dialog open={target !== null} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent data-testid="publish-confirm-dialog">
        <DialogHeader>
          <DialogTitle>{t('dashboard.publish_dialog.title')}</DialogTitle>
          <DialogDescription>
            {t('dashboard.publish_dialog.body', { name: target?.name ?? '' })}
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center space-x-2 py-4">
          <Checkbox
            id="publish-registration"
            data-testid="publish-registration-checkbox"
            checked={withRegistration}
            onCheckedChange={(checked) => setWithRegistration(checked === true)}
          />
          <Label htmlFor="publish-registration">
            {t('dashboard.publish_dialog.open_registration_label')}
          </Label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            {t('dashboard.publish_dialog.cancel')}
          </Button>
          <Button data-testid="confirm-publish-button" onClick={onConfirm}>
            {t('dashboard.publish_dialog.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
